/**
 * Route tree — hand-authored (M1 skeleton; auth restructure in Story ADMIN-W4;
 * IA route map + redirects in Story UI-U2). TanStack Router file-based routing:
 * in production add @tanstack/router-vite-plugin to vite.config.ts so this file
 * is auto-regenerated on `pnpm dev`.
 *
 * Structure (ADMIN-W4 + UI-U2):
 *   root                      — bare <Outlet/> (no shell)
 *   ├─ 'app' layout route     — <AppShell> + auth gate (requireBrokerSession):
 *   │    │                      no broker session → /login preserving the intended path
 *   │    ├─ canonical routes  — '/' Heute, /kommunikation, /posteingang(+$id),
 *   │    │                      /aufgaben, /rechnungen, /courtage, /portfolio,
 *   │    │                      /kunden(+$id), /policen(+$id), /ausschreibungen(+$id),
 *   │    │                      /versicherer(+$id), /renewals, /brok
 *   ├─ 'admin-layout' route   — <AdminShell> + gate (UI-U6): /admin (index →
 *   │                           first visible section) + /admin/$section
 *   │    └─ redirect routes   — /inbox → /kommunikation (→ /posteingang while it is
 *   │                           switched off), /tasks → /aufgaben,
 *   │                           /customers(+$id) → /kunden(+$id)  (replace: true;
 *   │                           REQUIRED: the api search module emits the OLD paths
 *   │                           server-side — see search-query.service.ts)
 *   ├─ /login                 — shell-less OIDC entry (LoginScreen)
 *   ├─ /auth/callback         — the code+PKCE redirect target (AuthCallback)
 *   └─ /einladung?token=…     — broker-invitation activation (EinladungPage)
 */

import {
  createRootRouteWithContext,
  createRoute,
  Outlet,
  redirect,
} from '@tanstack/react-router';
import type { QueryClient } from '@tanstack/react-query';
import {
  POSTEINGANG_KANAL_VALUES,
  POSTEINGANG_STATE_VALUES,
  type PosteingangKanal,
  type PosteingangState,
} from '@brokly/shared';
import { InboxPage } from './routes/inbox';
import { AppShell } from './app/shell/AppShell';
import { DashboardScreen } from './features/dashboard/DashboardScreen';
import { CustomersScreen } from './features/customers/CustomersScreen';
import { PoliciesScreen } from './features/policies/PoliciesScreen';
import { CarriersScreen } from './features/carriers/CarriersScreen';
import { InvoicesScreen } from './features/invoices/InvoicesScreen';
import { CourtageScreen } from './features/courtage/CourtageScreen';
import { PortfolioScreen } from './features/portfolio/PortfolioScreen';
import { KontrollListeScreen } from './features/control/KontrollListeScreen';
import { KontrollDetailScreen } from './features/control/KontrollDetailScreen';
import { RenewalsScreen } from './features/renewals/RenewalsScreen';
import { AufgabenScreen } from './features/aufgaben/AufgabenScreen';
import { BearbeitungsMaskeScreen } from './features/posteingang/BearbeitungsMaskeScreen';
import { PosteingangPage } from './features/posteingang/PosteingangPage';
import { KOMMUNIKATION_ENABLED } from './lib/feature-flags';
import { KundeDetailScreen } from './features/entity-detail/KundeDetailScreen';
import { AusschreibungScreen } from './features/placements/AusschreibungScreen';
import { AusschreibungenListScreen } from './features/placements/AusschreibungenListScreen';
import { PoliceDetailScreen } from './features/entity-detail/PoliceDetailScreen';
import { VersichererDetailScreen } from './features/entity-detail/VersichererDetailScreen';
import { DocumentsScreen } from './features/documents/DocumentsScreen';
import { DocumentViewerScreen } from './features/documents/DocumentViewerScreen';
import { BrokScreen } from './features/brok/BrokScreen';
import { EinstellungenScreen } from './features/settings/EinstellungenScreen';
import { AdminShell } from './features/administration/AdminShell';
import { AdminIndex, AdminSectionView } from './features/administration/AdminSection';
import { LoginScreen } from './features/auth/LoginScreen';
import { AuthCallback } from './features/auth/AuthCallback';
import { EinladungPage } from './features/auth/EinladungPage';
import { hasBrokerSession } from './features/auth/useBrokerAuth';

export interface RouterContext {
  queryClient: QueryClient;
}

// Root: bare outlet — the shell moved to the 'app' layout route (ADMIN-W4).
const rootRoute = createRootRouteWithContext<RouterContext>()({ component: Outlet });

// ── The authed app layout (AppShell + gate) ──────────────────────────────────

/**
 * Auth gate (ADMIN-W4, extracted in UI-U2): every app route requires a broker
 * session; the intended path is preserved and restored after the OIDC callback.
 * Exported so UI-U6's sibling admin layout route can reuse the same gate.
 */
export function requireBrokerSession(location: { href: string }): void {
  if (!hasBrokerSession()) {
    throw redirect({ to: '/login', search: { redirect: location.href } });
  }
}

function AppLayout() {
  return (
    <AppShell>
      <Outlet />
    </AppShell>
  );
}

const appLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'app',
  component: AppLayout,
  beforeLoad: ({ location }) => {
    requireBrokerSession(location);
  },
});

// ── Canonical app routes (UI-U2 route map) ───────────────────────────────────

const indexRoute = createRoute({ getParentRoute: () => appLayoutRoute, path: '/', component: DashboardScreen });
// Exported so EingangScreen can read ?tab= via kommunikationRoute.useSearch()
// (the rechnungenRoute precedent below, this route tree is hand-authored).
export const kommunikationRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/kommunikation',
  component: InboxPage,
  // Disconnected (lib/feature-flags.ts): the route is NOT registered while KOMMUNIKATION_ENABLED is
  // false (see the children list below), so /kommunikation does not exist — no page, no URL, no
  // redirect (Jevin #37: remove it, don't forward). The const + InboxPage stay so re-enabling is one
  // flag flip and EingangScreen can still read ?tab=/?mail= via kommunikationRoute.useSearch(). The
  // OAuth return, deep links and the Freigaben tab now point straight at their new homes
  // (/posteingang, /posteingang/$id, /aufgaben) from the callers, not through a /kommunikation hop.
  // Issue 33: the Freigabe-Anfrage notification deep-links to
  // /kommunikation?tab=freigaben, so the open tab has to be a real search param;
  // it used to be local component state only, and the link landed on Eingang.
  // `mail` = the COMMUNICATION id to preselect in the Eingang reading pane. The
  // Posteingang's Betreff links here (the mail as a broker reads it) while its row
  // opens the Bearbeitungsmaske — two destinations, so the mail one needs a real URL.
  validateSearch: (
    search: Record<string, unknown>,
  ): { tab?: string; mail?: string; mailbox?: string; reason?: string } => {
    const out: { tab?: string; mail?: string; mailbox?: string; reason?: string } = {};
    if (typeof search['tab'] === 'string') out.tab = search['tab'];
    if (typeof search['mail'] === 'string') out.mail = search['mail'];
    if (typeof search['mailbox'] === 'string') out.mailbox = search['mailbox'];
    if (typeof search['reason'] === 'string') out.reason = search['reason'];
    return out;
  },
});
/**
 * Posteingang — the Eingangsliste as its OWN Bereich (Navigationskonzept V3.0 §2.2),
 * no longer a tab inside /kommunikation. The offen/erledigt tab and the Kanal filter
 * are search params so a filtered Posteingang stays linkable.
 */
export const posteingangRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/posteingang',
  component: PosteingangPage,
  validateSearch: (
    search: Record<string, unknown>,
  ): { state?: PosteingangState; kanal?: PosteingangKanal } => {
    const out: { state?: PosteingangState; kanal?: PosteingangKanal } = {};
    if (POSTEINGANG_STATE_VALUES.includes(search['state'] as PosteingangState)) {
      out.state = search['state'] as PosteingangState;
    }
    if (POSTEINGANG_KANAL_VALUES.includes(search['kanal'] as PosteingangKanal)) {
      out.kanal = search['kanal'] as PosteingangKanal;
    }
    return out;
  },
});
/**
 * The Bearbeitungsmaske: one Eingang and every task it produced (epic WS-C). The
 * Eingang is the path segment; the task selected inside it is `?task=`, since picking
 * a sibling task moves WITHIN this Eingang rather than to another object.
 */
export const eingangDetailRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/posteingang/$id',
  component: BearbeitungsMaskeScreen,
  validateSearch: (search: Record<string, unknown>): { task?: string } =>
    typeof search['task'] === 'string' ? { task: search['task'] } : {},
});
const aufgabenRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/aufgaben',
  component: AufgabenScreen,
  // Bearbeitungsmaske deep-link → /aufgaben?task=<uuid> opens that task's peek.
  validateSearch: (search: Record<string, unknown>): { task?: string } =>
    typeof search['task'] === 'string' ? { task: search['task'] } : {},
});
const renewalsRoute = createRoute({ getParentRoute: () => appLayoutRoute, path: '/renewals', component: RenewalsScreen });
// Exported so InvoicesScreen can read ?invoice= via rechnungenRoute.useSearch()
// (the loginRoute.useSearch() precedent — this route tree is hand-authored).
export const rechnungenRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/rechnungen',
  component: InvoicesScreen,
  // Story UI-U4: Aufgaben deep-link → /rechnungen?invoice=<uuid> opens the peek.
  validateSearch: (search: Record<string, unknown>): { invoice?: string } =>
    typeof search['invoice'] === 'string' ? { invoice: search['invoice'] } : {},
});
// UI-U5: Courtage area is live (E11 made visible). UI-U7: Portfolio analytics is live.
const courtageRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/courtage',
  component: CourtageScreen,
});
const portfolioRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/portfolio',
  component: PortfolioScreen,
});
// Kontrollbereich Cockpit (Bereich 5, Kapitel 8) — children of /portfolio so the
// sidebar keeps "Portfolio" active (the /policen + /policen/$id precedent). The panel
// on the Portfolio Dashboard links here; the list filters by stage, the detail opens a row.
const kontrolleListeRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/portfolio/kontrolle/$stufe',
  component: KontrollListeScreen,
});
const kontrolleDetailRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/portfolio/kontrolle/$stufe/$id',
  component: KontrollDetailScreen,
});
const kundenRoute = createRoute({ getParentRoute: () => appLayoutRoute, path: '/kunden', component: CustomersScreen });
// Exported so KundeDetailScreen can seed its active tab from ?tab= (the policenDetailRoute
// precedent). P2 Placement Slice 4: «Zurück» from an Ausschreibung lands on /kunden/$id?tab=ausschreibungen.
export const kundenDetailRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/kunden/$id',
  component: KundeDetailScreen,
  validateSearch: (search: Record<string, unknown>): { tab?: string } => {
    const out: { tab?: string } = {};
    if (typeof search['tab'] === 'string') out.tab = search['tab'];
    return out;
  },
});
const policenRoute = createRoute({ getParentRoute: () => appLayoutRoute, path: '/policen', component: PoliciesScreen });
// Exported so PoliceDetailScreen can read ?tab= (the kommunikationRoute precedent above). P2
// Placement Slice 2: «Details je Police» deep-links to /policen/$id?tab=risiko.
export const policenDetailRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/policen/$id',
  component: PoliceDetailScreen,
  validateSearch: (search: Record<string, unknown>): { tab?: string } => {
    const out: { tab?: string } = {};
    if (typeof search['tab'] === 'string') out.tab = search['tab'];
    return out;
  },
});
// P2 Placement — the FIRM-WIDE Ausschreibungen list (sidebar entry «Ausschreibungen», the /policen
// precedent). The detail below is its child by path so the sidebar entry stays active there.
const ausschreibungenRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/ausschreibungen',
  component: AusschreibungenListScreen,
});
// P2 Placement Slice 4 — the full-screen Ausschreibung (Placement) detail. Own top-level route
// (not nested under /kunden) so Slices 5-15 deep-link to it directly; `?tab=` selects the 6-tab
// shell's active tab. Exported so AusschreibungScreen can read ?tab= (the policenDetailRoute precedent).
export const ausschreibungDetailRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/ausschreibungen/$id',
  component: AusschreibungScreen,
  validateSearch: (search: Record<string, unknown>): { tab?: string } => {
    const out: { tab?: string } = {};
    if (typeof search['tab'] === 'string') out.tab = search['tab'];
    return out;
  },
});
const versichererRoute = createRoute({ getParentRoute: () => appLayoutRoute, path: '/versicherer', component: CarriersScreen });
const versichererDetailRoute = createRoute({ getParentRoute: () => appLayoutRoute, path: '/versicherer/$id', component: VersichererDetailScreen });
// DMS M11: the Ablage grid + its reusable inline PDF viewer. `/ablage/$id` is
// deliberately a CHILD of `/ablage` (not a top-level `/dokument/$id`) so the
// sidebar's `isActive` prefix-match keeps "Ablage" highlighted while viewing a
// document, mirroring the `/policen` + `/policen/$id` precedent. M12 reuses this
// EXACT route (`navigate({ to: '/ablage/$id', params: { id: documentId } })`) to
// open a policy's PDF in the same viewer — no separate viewer route/component.
const ablageRoute = createRoute({ getParentRoute: () => appLayoutRoute, path: '/ablage', component: DocumentsScreen });
const ablageDetailRoute = createRoute({ getParentRoute: () => appLayoutRoute, path: '/ablage/$id', component: DocumentViewerScreen });
const brokRoute = createRoute({ getParentRoute: () => appLayoutRoute, path: '/brok', component: BrokScreen });
// Part C: the user-settings page (reached from the bottom-left profile area).
const einstellungenRoute = createRoute({ getParentRoute: () => appLayoutRoute, path: '/einstellungen', component: EinstellungenScreen });

// ── Redirect routes — old paths keep working (UI-U2 AC 6) ────────────────────
// REQUIRED, not cleanup: the api search module resolves hit routes SERVER-SIDE
// and emits /customers/<id> + /inbox (search-query.service.ts); feature screens
// still navigate() to the old paths. History is REPLACED so Back never lands
// on an old URL. Params pass through.

const inboxRedirectRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/inbox',
  beforeLoad: () => {
    // The api search module still emits /inbox (search-query.service.ts). It resolves to the live
    // inbox: /posteingang while Kommunikation is off, /kommunikation when it is re-enabled.
    throw redirect({ to: KOMMUNIKATION_ENABLED ? '/kommunikation' : '/posteingang', replace: true });
  },
});
const tasksRedirectRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/tasks',
  beforeLoad: () => {
    throw redirect({ to: '/aufgaben', replace: true });
  },
});
const customersRedirectRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/customers',
  beforeLoad: () => {
    throw redirect({ to: '/kunden', replace: true });
  },
});
const customersDetailRedirectRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/customers/$id',
  beforeLoad: ({ params }) => {
    throw redirect({ to: '/kunden/$id', params, replace: true });
  },
});

// ── The Administration layout (UI-U6) — AdminShell + gate ────────────────────
// A SIBLING of the `app` layout (child of root): the admin area REPLACES the app
// sidebar with its OWN settings nav (design.md §5), so it must NOT nest under
// AppShell. It reuses the same broker-session gate. Sibling story UI-U5 slots its
// /admin/tarifmatrix content in via adminNav.ts + the AdminSection page registry
// (no new route needed — $section covers it).

function AdminLayout() {
  return (
    <AdminShell>
      <Outlet />
    </AdminShell>
  );
}

const adminLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'admin-layout',
  component: AdminLayout,
  beforeLoad: ({ location }) => {
    requireBrokerSession(location);
  },
});

const adminIndexRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: '/admin',
  component: AdminIndex,
});

const adminSectionRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: '/admin/$section',
  component: function AdminSectionRouteComponent() {
    const { section } = adminSectionRoute.useParams();
    return <AdminSectionView section={section} />;
  },
});

// ── Shell-less auth routes (ADMIN-W4) ────────────────────────────────────────

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  validateSearch: (search: Record<string, unknown>): { redirect?: string } =>
    typeof search['redirect'] === 'string' ? { redirect: search['redirect'] } : {},
  component: function LoginRoute() {
    const { redirect: redirectPath } = loginRoute.useSearch();
    return <LoginScreen redirect={redirectPath} />;
  },
});

const authCallbackRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/auth/callback',
  validateSearch: (search: Record<string, unknown>): { code?: string; state?: string } => ({
    ...(typeof search['code'] === 'string' ? { code: search['code'] } : {}),
    ...(typeof search['state'] === 'string' ? { state: search['state'] } : {}),
  }),
  component: function AuthCallbackRoute() {
    const { code, state } = authCallbackRoute.useSearch();
    return <AuthCallback code={code} state={state} />;
  },
});

const einladungRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/einladung',
  validateSearch: (search: Record<string, unknown>): { token?: string } =>
    typeof search['token'] === 'string' ? { token: search['token'] } : {},
  component: function EinladungRoute() {
    const { token } = einladungRoute.useSearch();
    return <EinladungPage token={token} />;
  },
});

export const routeTree = rootRoute.addChildren([
  appLayoutRoute.addChildren([
    indexRoute,
    // Registered ONLY while Kommunikation is enabled; otherwise /kommunikation does not exist (Jevin #37).
    ...(KOMMUNIKATION_ENABLED ? [kommunikationRoute] : []),
    posteingangRoute,
    eingangDetailRoute,
    aufgabenRoute,
    renewalsRoute,
    rechnungenRoute,
    courtageRoute,
    portfolioRoute,
    kontrolleListeRoute,
    kontrolleDetailRoute,
    kundenRoute,
    kundenDetailRoute,
    policenRoute,
    policenDetailRoute,
    ausschreibungenRoute,
    ausschreibungDetailRoute,
    versichererRoute,
    versichererDetailRoute,
    ablageRoute,
    ablageDetailRoute,
    brokRoute,
    einstellungenRoute,
    inboxRedirectRoute,
    tasksRedirectRoute,
    customersRedirectRoute,
    customersDetailRedirectRoute,
  ]),
  adminLayoutRoute.addChildren([adminIndexRoute, adminSectionRoute]),
  loginRoute,
  authCallbackRoute,
  einladungRoute,
]);
